/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useDateFormatter, useLocale } from 'react-aria';
import { type CalendarState, type RangeCalendarState } from 'react-stately';
import { startOfWeek, today } from '@internationalized/date';

interface WeekDaysHeaderProps {
    state: RangeCalendarState | CalendarState;
}

/**
 * A single row of weekday labels shared by every month, mirroring how
 * `useCalendarGrid` builds its `weekDays`. Hidden from screen readers for the
 * same reason the per-grid header is: each cell's label already names the day.
 */
export function WeekDaysHeader({ state }: WeekDaysHeaderProps) {
    const { locale } = useLocale();
    const dayFormatter = useDateFormatter({
        weekday: 'narrow',
        timeZone: state.timeZone,
    });

    const weekDays = React.useMemo(() => {
        const weekStart = startOfWeek(today(state.timeZone), locale);
        return [...new Array(7).keys()].map((index) =>
            dayFormatter.format(weekStart.add({ days: index }).toDate(state.timeZone))
        );
    }, [locale, state.timeZone, dayFormatter]);

    return (
        <div className="calendar-weekdays" aria-hidden>
            <div className="calendar-table">
                <div className="tr">
                    {weekDays.map((day, index) => (
                        <div className="th" key={index}>
                            {day}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
